"use client";

import React, { useEffect, useRef, useState } from "react";
import { TrendingUp, House, Clock, MapPin } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const marketData = [{
  id: "co2-reduction",
  name: "Eliminacion de CO2",
  description: "Ahorros en CO2 proyectados por galon de Biodiesel producido por BioPower",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_co2_reduction.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_co2_reduction.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_co2_reduction.png-wvc-sizes",
    alt: "CO2 Reduction Statistics"
  },
  metrics: {
    avgPrice: "78%",
    dom: "2.5kg",
    inventory: "High"
  },
  trend: "up"
}, {
  id: "global-adoption",
  name: "Adopcion Global",
  description: "Crecimiento global en uso de energías renovables en industrias ligeras y pesadas",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_global_adoption.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_global_adoption.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_global_adoption.png-wvc-sizes",
    alt: "Global Renewable Energy Growth"
  },
  metrics: {
    avgPrice: "12.4%",
    dom: "2024",
    inventory: "Rising"
  },
  trend: "up"
}, {
  id: "cuba-energy",
  name: "\xA0Independencia Energetica para Cuba\xA0",
  description: "Reducimos la dependencia en importaciones de petroleo",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/cuba_flag.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/cuba_flag.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/cuba_flag.png-wvc-sizes",
    alt: "Cuban flag"
  },
  metrics: {
    avgPrice: "15MW",
    dom: "2030",
    inventory: "Target"
  },
  trend: "neutral"
}, {
  id: "waste-recovery",
  name: "Recuperacion de Desechos",
  description: "Convertimos y reformamos aceites de cocina y de motores\xA0",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_waste_recovery.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_waste_recovery.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_waste_recovery.png-wvc-sizes",
    alt: "Waste Oil Recovery Process"
  },
  metrics: {
    avgPrice: "94%",
    dom: "24h",
    inventory: "Optimal"
  },
  trend: "up"
}, {
  id: "biomass-utilization",
  name: "Energias Renovables\xA0",
  description: "Generamos electricidad a partir de energías renovables",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_biomass_utilization.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_biomass_utilization.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_biomass_utilization.png-wvc-sizes",
    alt: "Biomass Processing"
  },
  metrics: {
    avgPrice: "4.2T",
    dom: "365",
    inventory: "Stable"
  },
  trend: "up"
}, {
  id: "biopower-overall",
  name: "Impacto BioPower\xA0",
  description: "Reduccion de CO2\xA0",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_biopower_overall.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_biopower_overall.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/impact_biopower_overall.png-wvc-sizes",
    alt: "BioPower Overall Impact"
  },
  metrics: {
    avgPrice: "1.2M",
    dom: "Cuba",
    inventory: "Scaling"
  },
  trend: "up",
  isHighlight: true
}];
export default function GlobalImpactMetrics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="254121" id="global-impact-metrics" className="bg-background text-foreground relative overflow-hidden py-16 md:py-24 lg:py-32">
      <div className="bg-primary/10 absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-lg blur-3xl pointer-events-none" />
      <div className="bg-secondary/10 absolute bottom-0 right-0 translate-x-1/3 translate-y-1/3 h-[600px] w-[600px] rounded-lg blur-3xl pointer-events-none" />

      <div ref={ref} className={`container mx-auto px-4 md:px-6 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground font-default">Impacto Global</Badge>
            <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-4">Estadisticas de Energias Renovables&nbsp;</h2>
            <p className="text-muted-foreground text-lg md:text-xl font-default">Seguimiento del crecimiento de energías renovables a nivel mundial y su papel crítico en la reducción de emisiones de CO2 para un futuro sostenible.</p>
          </div>
          <div className="shrink-0">
            <Button asChild variant="outline" className="group border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md">
              <Link to="https://www.moeveglobal.com/es/planet-energy/objetivos-2030/informe-onu-punto-inflexion-energias-renovables-2026">
                Reporte de Impacto Global
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {marketData.map((market, index) => <Card key={market.id} data-index={index} className={`flex flex-col overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-border/50 rounded-lg ${market.isHighlight ? "ring-2 ring-primary/30 bg-accent/30" : "bg-card"}`}>
              <div className="relative">
                <div className="aspect-video overflow-hidden">
                  <img src={market.image.src} data-wvc-srcset={market.image.srcset} data-wvc-sizes={market.image.sizes} alt={market.image.alt} className="object-cover w-full h-full transition-transform duration-500 hover:scale-105" loading="lazy" />
                </div>
                <div className="absolute top-3 right-3">
                  {market.trend === "up" && <Badge className="bg-background/90 text-foreground backdrop-blur-sm border-none shadow-md font-default">
                      <TrendingUp className="mr-1 h-3 w-3 text-primary" />
                      Growing
                    </Badge>}
                </div>
              </div>

              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="h-4 w-4 text-secondary" />
                  <CardTitle className="font-default text-xl font-bold">{market.name}</CardTitle>
                </div>
                <CardDescription className="text-sm line-clamp-2 font-default text-muted-foreground">
                  {market.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="mt-auto pb-6">
                <Separator className="mb-4 bg-border/50" />
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="flex flex-col items-center justify-center p-2 rounded-md bg-muted/50">
                    <span className="text-xs uppercase tracking-widest text-muted-foreground font-mono font-bold mb-1">Reduction</span>
                    <span className="font-bold text-foreground text-sm md:text-base font-mono">{market.metrics.avgPrice}</span>
                  </div>
                  <div className="flex flex-col items-center justify-center p-2 rounded-md bg-muted/50">
                    <span className="text-xs uppercase tracking-widest text-muted-foreground font-mono font-bold mb-1 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Year
                    </span>
                    <span className="font-bold text-foreground text-sm md:text-base font-mono">{market.metrics.dom}</span>
                  </div>
                  <div className="flex flex-col items-center justify-center p-2 rounded-md bg-muted/50">
                    <span className="text-xs uppercase tracking-widest text-muted-foreground font-mono font-bold mb-1 flex items-center gap-1">
                      <House className="h-3 w-3" /> Status
                    </span>
                    <span className="font-bold text-foreground text-sm md:text-base font-mono">{market.metrics.inventory}</span>
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}